import { AnalyticsData, CreditTerms, Offer } from '../../domain/qualification';
import { selectBusinessInfo } from '../../features/application/reducers/business-info';
import { formatBillingCycle } from '../../general/utils/formatters';
import { RootState } from '../../store';

const centsToDollars = (cents: number | null) => (cents ? cents / 100 : null);

const transformedCreditCoefficient: Record<string, number> = {
  weekly: 2,
  biweekly: 1.5,
  monthly: 1,
};

const calculateFinalCreditLimitTransformed = (finalCreditLimit: number, billingCycle: number) => {
  const billingCycleInEnglish = formatBillingCycle(billingCycle);

  return +(finalCreditLimit * 0.055 * transformedCreditCoefficient[billingCycleInEnglish]).toFixed(2);
};

const createTQC03Properties = (
  state: RootState,
): {
  annualrevenue: number | null;
  fuelspend: number | null;
  fleet_size: number | null;
} => {
  const { businessInfo } = selectBusinessInfo(state);
  return {
    annualrevenue: centsToDollars(businessInfo.annualRevenue),
    fuelspend: centsToDollars(businessInfo.monthlyFuelSpend),
    fleet_size: businessInfo.fleetSize,
  };
};

const flattenOffers = (offers: Offer[]): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  for (const offer of offers) {
    const prefix = `credit_terms_offer_${offer.offerIndex}`;
    result[`${prefix}_billing_cycle`] = formatBillingCycle(offer.billingCycle);
    result[`${prefix}_grace_period_days`] = offer.gracePeriodDays;
    result[`${prefix}_user_fee`] = offer.userFee;
    result[`${prefix}_fuel_rebate_hundredths_of_cent`] = offer.fuelRebateHundredthsOfCent;
    result[`${prefix}_non_fuel_rebate_basis_points`] = offer.nonFuelRebateBasisPoints;
  }

  return result;
};

type TQC04Payload = {
  billing_cycle: string;
  final_credit_limit: number;
  final_credit_limit_trans: number;
  applicant_email: string;
  company_name: string;
  [key: string]: string | number | undefined;
};

const createTQC04Properties = ({
  creditTerms,
  state,
  offers,
}: {
  creditTerms: Pick<CreditTerms, 'billingCycle' | 'finalCreditLimit'>;
  state: RootState;
  offers?: Offer[];
}): TQC04Payload => {
  const billingCycleToEnglish = formatBillingCycle(creditTerms.billingCycle);
  const finalCreditLimitTransformed = calculateFinalCreditLimitTransformed(
    creditTerms.finalCreditLimit,
    creditTerms.billingCycle,
  );
  const applicant_email = state.preliminaryInfo.info.email;
  const { businessInfo } = selectBusinessInfo(state);

  const offerProperties = offers?.length ? flattenOffers(offers) : {};

  return {
    billing_cycle: billingCycleToEnglish,
    final_credit_limit: centsToDollars(creditTerms.finalCreditLimit)!,
    final_credit_limit_trans: centsToDollars(finalCreditLimitTransformed)!,
    applicant_email,
    company_name: businessInfo.name,
    ...offerProperties,
  };
};

type TQC06Payload = {
  offer_selected: string;
};

const createTQC06Properties = (offerSelected: number): TQC06Payload => ({
  offer_selected: `credit_terms_offer_${offerSelected}`,
});

const createDQC01Traits = ({
  analyticsData,
  state,
}: {
  analyticsData: AnalyticsData;
  state: RootState;
}): {
  phone: string;
  email: string;
  promo_code?: string;
  referral_rock_id?: string;
  customer_id: string;
  user_exists?: boolean;
} => {
  const { phone, email } = state.preliminaryInfo.info;
  const promo_code = state.promotion.promotion?.promoCode;
  const referral_rock_id = state.referrer.referrerId;
  const user_exists = analyticsData.fleetUserExists;
  return {
    phone,
    email,
    promo_code,
    referral_rock_id,
    user_exists,
    customer_id: analyticsData.customerId,
  };
};

const createDQC10Traits = (
  state: RootState,
): {
  email: string;
  promo_code?: string;
  referral_rock_id?: string;
} => {
  const { email } = state.preliminaryInfo.info;
  const promo_code = state.promotion.promotion?.promoCode;
  const referral_rock_id = state.referrer.referrerId;
  return {
    email,
    promo_code,
    referral_rock_id,
  };
};

export { createTQC03Properties, createTQC04Properties, createTQC06Properties, createDQC01Traits, createDQC10Traits };
